import { buildPolarityById, POSITIVE, NEGATIVE, NEUTRAL } from "../sentiment";
import { CONFIG } from "./CONFIG";

// Cooccurrence sides for per-feature sentiment: features × (emotion/affect | signals),
// feature pinned as tagA. ONE such call gives BOTH the per-feature mention count
// (cooccurrence.tagCounts[featureId]) AND the sentiment distribution (the
// feature→emotion/signal pairs) — same shape the Signals widget uses for topics.
export const featureSentimentSides = (source = CONFIG.sentimentSource) => ({
  sideA: { subThemeTagIds: ["features"] },
  sideB: { subThemeTagIds: [source === "emotionAffect" ? "emotion_affect" : "signals"] },
});

// Assemble the per-feature spine — driven off the FULL feature set, enriched with:
// standalone mention total (cooccurrence.tagCounts) and a sentiment breakdown rolled
// up from the feature→(emotion|signal) pairs via the shared polarity lookup. Only
// features that occurred this window (total > 0) are kept. Health = (positive −
// negative) / classified, in [-1, 1]. No trend — v1's feature widget shows none.
export const assembleFeatures = (
  featureList,
  cooccurrence,
  { signals = [], emotionAffect = [] } = {},
  source = CONFIG.sentimentSource
) => {
  const tagCounts = cooccurrence?.tagCounts || {};
  const polarityById = buildPolarityById(source, { signals, emotionAffect });

  // Per-feature polarity distribution from the pinned pairs (feature = tagA).
  const sentimentByFeature = {};
  for (const p of cooccurrence?.data || []) {
    const fid = p.tagA?._id;
    if (!fid) continue;
    const pol = polarityById[String(p.tagB?._id)] || NEUTRAL;
    const b = (sentimentByFeature[fid] ||= { [POSITIVE]: 0, [NEGATIVE]: 0, [NEUTRAL]: 0 });
    b[pol] += p.count;
  }

  return featureList
    .map((f) => {
      const id = f._id;
      const total = tagCounts[id] ?? 0;
      const s = sentimentByFeature[id] || { [POSITIVE]: 0, [NEGATIVE]: 0, [NEUTRAL]: 0 };
      const classified = s[POSITIVE] + s[NEGATIVE] + s[NEUTRAL];
      const health = classified ? (s[POSITIVE] - s[NEGATIVE]) / classified : 0;
      return {
        feature: f.display,
        featureId: id,
        total,
        sentiment: {
          positive: s[POSITIVE],
          negative: s[NEGATIVE],
          neutral: s[NEUTRAL],
          total: classified,
          health, // [-1, 1]; FE maps to a positive/negative/neutral status pill
        },
      };
    })
    .filter((f) => f.total > 0)
    .sort((a, b) => b.total - a.total);
};
